import { applyRate } from './money';
import { BASIS_POINT_DIVISOR } from './constants';
import type { DiscountType, OrderItemStatus, TaxType } from './constants';

/**
 * The single source of truth for order arithmetic.
 *
 * Every input is a *snapshot*: unit price, modifier price deltas and the tax
 * rate are all values that were copied onto the order when the item was added.
 * Nothing here reads the live menu or the live settings, so recomputing the
 * totals of a historical order always reproduces the original figures even if
 * prices, tax rules or the service charge have changed since.
 */

export interface PricedModifier {
  /** Snapshot of the modifier option's price adjustment, in cents. */
  priceDeltaCents: number;
  quantity: number;
}

export interface PricedItem {
  quantity: number;
  /** Snapshot of the menu item price at the time it was added, in cents. */
  unitPriceCents: number;
  taxType: TaxType;
  /** Snapshot of the tax rate at the time it was added, in basis points. */
  taxRateBp: number;
  status: OrderItemStatus;
  modifiers?: readonly PricedModifier[];
  /**
   * A discount on this one line, as staff entered it — a complaint, a slow
   * dish, or a dish put on the house at 100%. Comes off before the bill-level
   * discount, which is then worked out on what is left.
   */
  discount?: OrderDiscount | null;
}

export interface LineTotals {
  /** unit price + modifier deltas, before quantity. */
  effectiveUnitPriceCents: number;
  modifiersTotalCents: number;
  /** Before any discount. */
  lineSubtotalCents: number;
  /** This line's own discount, if one was put on the item itself. */
  lineItemDiscountCents: number;
  /**
   * Every discount on this line: its own, plus its share of the bill-level
   * one. The e-invoice, the receipt and the nightly dashboard push all read
   * this as "what came off this line", so it is deliberately the total.
   */
  lineDiscountCents: number;
  /** Tax on the discounted amount, at the line's own snapshotted rate. */
  lineTaxCents: number;
  /** (subtotal - discount) + tax. The service charge is not part of it. */
  lineTotalCents: number;
  /**
   * This line's share of the service charge, and the VAT on that share at the
   * line's own rate. Both 0 unless the order taxes its service charge.
   */
  lineServiceChargeCents: number;
  lineServiceTaxCents: number;
}

/** A discount as entered by staff: a percentage (basis points) or a fixed money amount (cents). */
export interface OrderDiscount {
  type: DiscountType;
  /** Basis points when `type` is PERCENT, cents when FIXED. */
  value: number;
}

export interface TaxBucket {
  taxType: TaxType;
  rateBp: number;
  baseCents: number;
  taxCents: number;
}

export interface OrderTotals {
  /** Item sales before discount, service charge and tax. */
  subtotalCents: number;
  /** Describes the *bill-level* discount only; `billDiscountCents` is its amount. */
  discountType: DiscountType | null;
  /** The entered value: basis points for PERCENT, cents for FIXED. 0 when there is no discount. */
  discountValue: number;
  /**
   * Every discount on the order: the per-item ones plus the bill-level one.
   * Reporting, the e-invoice and the dashboard push read this figure, so it is
   * deliberately the total; the two parts below say how it was made up.
   */
  discountCents: number;
  /** The part that came from discounts on individual items. */
  itemDiscountCents: number;
  /** The part that came from the bill-level discount (`discountType`/`discountValue`). */
  billDiscountCents: number;
  serviceChargeBp: number;
  serviceChargeCents: number;
  taxCents: number;
  taxBreakdown: TaxBucket[];
  grandTotalCents: number;
}

/** Items in these states no longer contribute to the bill (but are kept on record). */
export function isBillable(status: OrderItemStatus): boolean {
  return status !== 'CANCELLED';
}

export function computeLineTotals(item: PricedItem, billShareCents = 0): LineTotals {
  const modifiersTotalPerUnit = (item.modifiers ?? []).reduce(
    (sum, m) => sum + m.priceDeltaCents * m.quantity,
    0,
  );
  const effectiveUnitPriceCents = item.unitPriceCents + modifiersTotalPerUnit;
  const lineSubtotalCents = effectiveUnitPriceCents * item.quantity;
  /* The line's own discount comes off first and is capped at the line, so a
     fixed amount larger than the dish can never make it negative. */
  const lineItemDiscountCents = computeDiscountCents(lineSubtotalCents, item.discount);
  const lineDiscountCents = lineItemDiscountCents + billShareCents;
  // Tax is computed per line, from that line's own snapshotted rate, on what
  // the guest actually pays for the line — i.e. after every discount on it.
  const lineTaxCents = applyRate(lineSubtotalCents - lineDiscountCents, item.taxRateBp);
  return {
    effectiveUnitPriceCents,
    modifiersTotalCents: modifiersTotalPerUnit * item.quantity,
    lineSubtotalCents,
    lineItemDiscountCents,
    lineDiscountCents,
    lineTaxCents,
    lineTotalCents: lineSubtotalCents - lineDiscountCents + lineTaxCents,
    lineServiceChargeCents: 0,
    lineServiceTaxCents: 0,
  };
}

/** How an order is priced beyond its items. */
export interface PricingOptions {
  serviceChargeBp: number;
  discount?: OrderDiscount | null;
  /**
   * Charge VAT on the service charge too, the way Vietnamese VAT treats a
   * service charge collected on top of the price. Worked per tax group
   * (e.g. food 8%, alcohol 10%), in two steps:
   *   1. group service charge = group amount x service rate
   *   2. group VAT            = (group amount + group service charge) x VAT rate
   * Snapshotted per order, so older bills keep the rule they were sold under.
   */
  taxServiceCharge?: boolean;
  /**
   * With `taxServiceCharge`: the unit, in cents, that the discount, each group's
   * service charge and each group's VAT are rounded to — 100 for whole đồng.
   * Keeps the bill, the receipt and the e-invoice equal to the đồng.
   */
  roundingUnitCents?: number;
}

/** `amount x rate`, rounded half away from zero to a multiple of `unit` cents. */
function applyRateToUnit(amountCents: number, rateBasisPoints: number, unit: number): number {
  if (unit <= 1) return applyRate(amountCents, rateBasisPoints);
  const product = amountCents * rateBasisPoints;
  const sign = product < 0 ? -1 : 1;
  return sign * Math.round(Math.abs(product) / (BASIS_POINT_DIVISOR * unit)) * unit;
}

/**
 * Split `totalCents` over `weights` like `allocateProportionally`, but also
 * when the total is negative (a rounding remainder can be), so the parts
 * always add back to exactly the total.
 */
function allocateSigned(totalCents: number, weights: readonly number[]): number[] {
  if (totalCents >= 0) return allocateProportionally(totalCents, weights);
  return allocateProportionally(-totalCents, weights).map((part) => -part);
}

/** How much a discount takes off a given subtotal. A fixed amount can never exceed the subtotal. */
export function computeDiscountCents(
  subtotalCents: number,
  discount: OrderDiscount | null | undefined,
): number {
  if (!discount || subtotalCents <= 0 || discount.value <= 0) return 0;
  const raw = discount.type === 'PERCENT' ? applyRate(subtotalCents, discount.value) : discount.value;
  return Math.min(raw, subtotalCents);
}

/**
 * Split `totalCents` across `weights` proportionally, in whole cents, so the
 * parts always add back up to exactly `totalCents` (largest-remainder method).
 * Rounding each line independently would drift by a cent or two, and the tax
 * lines would then no longer reconcile with the order discount.
 */
export function allocateProportionally(totalCents: number, weights: readonly number[]): number[] {
  const weightSum = weights.reduce((sum, w) => sum + w, 0);
  if (totalCents <= 0 || weightSum <= 0) return weights.map(() => 0);

  // BigInt: discount x line subtotal overflows 2^53 on large VND-scale bills.
  const total = BigInt(totalCents);
  const sum = BigInt(weightSum);
  const parts = weights.map((w) => {
    const product = total * BigInt(w);
    return { floor: Number(product / sum), remainder: product % sum };
  });

  const result = parts.map((part) => part.floor);
  let leftover = totalCents - result.reduce((acc, value) => acc + value, 0);
  const order = parts
    .map((part, index) => ({ index, remainder: part.remainder }))
    .sort((a, b) =>
      a.remainder === b.remainder ? a.index - b.index : a.remainder > b.remainder ? -1 : 1,
    );
  for (const { index } of order) {
    if (leftover <= 0) break;
    if (weights[index]! > 0) {
      result[index]! += 1;
      leftover -= 1;
    }
  }
  return result;
}

export interface OrderBreakdown {
  totals: OrderTotals;
  /** Index-aligned with the `items` passed in; non-billable items carry no discount. */
  lines: LineTotals[];
}

/**
 * Compute the full order totals together with each line's own figures.
 *
 * Order of operations, which matters because tax is involved:
 *   1. subtotal        = sum of the billable line subtotals
 *   2. discount        = a percentage of the subtotal, or a fixed amount (capped)
 *   3. the discount is split across the lines in proportion to their subtotals
 *   4. tax             = per line, at that line's own rate, on (subtotal - its discount share)
 *   5. service charge  = on the discounted subtotal
 *   6. with `taxServiceCharge`, steps 4-5 are worked per tax group instead:
 *      service = group amount x rate, VAT = (group amount + service) x VAT rate
 *
 * A discount therefore always lowers the taxable base — the guest is never taxed
 * on money they were not charged — and a mixed food/alcohol order keeps each
 * rate on its own share. Tax is never applied as one blended percentage.
 */
export function computeOrderBreakdown(
  items: readonly PricedItem[],
  options: PricingOptions,
): OrderBreakdown {
  const discount = options.discount ?? null;
  const taxService = Boolean(options.taxServiceCharge);
  const unit = taxService ? Math.max(1, Math.trunc(options.roundingUnitCents ?? 1)) : 1;
  const billableIndexes = items.flatMap((item, index) => (isBillable(item.status) ? [index] : []));
  const undiscounted = billableIndexes.map((index) => computeLineTotals(items[index]!));

  const subtotalCents = undiscounted.reduce((sum, line) => sum + line.lineSubtotalCents, 0);
  /* Discounts already given on individual lines. The bill-level discount is
     worked out on what is left after them — the house does not give a
     percentage off money it has already given away — and is spread across the
     lines by what each still owes, for the same reason. With no item discounts
     both figures are identical to the plain subtotal, so every existing bill
     prices exactly as it did before. */
  const itemDiscountCents = undiscounted.reduce((sum, line) => sum + line.lineItemDiscountCents, 0);
  const billableCents = subtotalCents - itemDiscountCents;
  const weights = undiscounted.map((line) => line.lineSubtotalCents - line.lineItemDiscountCents);
  let billDiscountCents = computeDiscountCents(billableCents, discount);
  let shares: number[];
  const unitDiscount = Math.min(Math.round(billDiscountCents / unit) * unit, billableCents);
  if (unit > 1 && unitDiscount % unit === 0) {
    // Whole units: the discount, and every line's share of it, stay in whole đồng.
    billDiscountCents = unitDiscount;
    shares = allocateProportionally(billDiscountCents / unit, weights).map((part) => part * unit);
  } else {
    shares = allocateProportionally(billDiscountCents, weights);
  }
  const discountCents = itemDiscountCents + billDiscountCents;

  const lines: LineTotals[] = items.map((item) => computeLineTotals(item));
  const buckets = new Map<string, TaxBucket>();
  /** Billable line positions in each tax group, for spreading the group figures back onto lines. */
  const groupLines = new Map<string, number[]>();

  billableIndexes.forEach((itemIndex, position) => {
    const item = items[itemIndex]!;
    const line = computeLineTotals(item, shares[position]!);
    lines[itemIndex] = line;

    const key = `${item.taxType}:${item.taxRateBp}`;
    const bucket = buckets.get(key) ?? {
      taxType: item.taxType,
      rateBp: item.taxRateBp,
      baseCents: 0,
      taxCents: 0,
    };
    // `baseCents` is the taxable base, i.e. after the discount.
    bucket.baseCents += line.lineSubtotalCents - line.lineDiscountCents;
    bucket.taxCents += line.lineTaxCents;
    buckets.set(key, bucket);
    groupLines.set(key, [...(groupLines.get(key) ?? []), itemIndex]);
  });

  const discountedSubtotalCents = subtotalCents - discountCents;
  let serviceChargeCents: number;

  if (taxService) {
    /* Per tax group, as the VAT rules work it: service on the group's amount,
       then VAT on the group's amount plus its service. */
    serviceChargeCents = 0;
    for (const [key, bucket] of buckets) {
      const goodsCents = bucket.baseCents;
      const groupServiceCents = applyRateToUnit(goodsCents, options.serviceChargeBp, unit);
      const groupTaxCents = applyRateToUnit(goodsCents + groupServiceCents, bucket.rateBp, unit);

      /* Hand the group figures back to its lines so any per-line view (the
         invoice, the daily report) adds up to exactly the group. The VAT left
         over after each line's own VAT is the VAT on the service charge. */
      const indexes = groupLines.get(key)!;
      const lineNets = indexes.map((i) => lines[i]!.lineSubtotalCents - lines[i]!.lineDiscountCents);
      const serviceShares = allocateProportionally(groupServiceCents, lineNets);
      const goodsTaxCents = indexes.reduce((sum, i) => sum + lines[i]!.lineTaxCents, 0);
      const serviceTaxShares = allocateSigned(groupTaxCents - goodsTaxCents, lineNets);
      indexes.forEach((lineIndex, n) => {
        lines[lineIndex] = {
          ...lines[lineIndex]!,
          lineServiceChargeCents: serviceShares[n]!,
          lineServiceTaxCents: serviceTaxShares[n]!,
        };
      });

      bucket.baseCents = goodsCents + groupServiceCents;
      bucket.taxCents = groupTaxCents;
      serviceChargeCents += groupServiceCents;
    }
  } else {
    serviceChargeCents = applyRate(discountedSubtotalCents, options.serviceChargeBp);
  }

  const taxBreakdown = [...buckets.values()].sort(
    (a, b) => a.taxType.localeCompare(b.taxType) || a.rateBp - b.rateBp,
  );
  const taxCents = taxBreakdown.reduce((sum, b) => sum + b.taxCents, 0);
  const applied = discount !== null && billDiscountCents > 0;

  return {
    lines,
    totals: {
      subtotalCents,
      discountType: applied ? discount.type : null,
      discountValue: applied ? discount.value : 0,
      discountCents,
      itemDiscountCents,
      billDiscountCents,
      serviceChargeBp: options.serviceChargeBp,
      serviceChargeCents,
      taxCents,
      taxBreakdown,
      grandTotalCents: discountedSubtotalCents + serviceChargeCents + taxCents,
    },
  };
}

/** One unit's exact share of a line, for splitting a bill by item. */
export interface UnitAmount {
  subtotalCents: number;
  /** This unit's share of every discount on the line. */
  discountCents: number;
  /** Of that, the part from a discount on the item itself — for reporting the split. */
  itemDiscountCents: number;
  taxCents: number;
  serviceChargeCents: number;
  serviceTaxCents: number;
  /** subtotal - discount + tax, mirroring `LineTotals.lineTotalCents` (service charge tracked separately). */
  totalCents: number;
}

/** The subset of `LineTotals` that splitting a line into units actually needs. */
export type SplittableLine = Pick<
  LineTotals,
  | 'lineSubtotalCents'
  | 'lineItemDiscountCents'
  | 'lineDiscountCents'
  | 'lineTaxCents'
  | 'lineServiceChargeCents'
  | 'lineServiceTaxCents'
>;

/**
 * Each line's full share of the service charge (the charge itself, plus any
 * VAT on it) — regardless of whether the order prices that per line or as one
 * order-level figure.
 *
 * `computeOrderBreakdown` only fills in `lineServiceChargeCents` /
 * `lineServiceTaxCents` when `taxServiceCharge` is on; the far more common
 * case charges a single order-level `serviceChargeCents` with no per-line
 * breakdown at all (`LineTotals` deliberately leaves both at 0 then — see its
 * doc comment). A bill split by item still has to collect its fair share of
 * the service charge either way, so this spreads the order-level figure by
 * each line's net (pre-tax, pre-service) weight when there is no per-line
 * figure to use directly — the same weighting the discount above already
 * uses.
 */
export function allocateServiceChargeToLines(
  options: { serviceChargeCents: number; taxServiceCharge?: boolean },
  lines: readonly SplittableLine[],
): number[] {
  if (options.taxServiceCharge) {
    return lines.map((line) => line.lineServiceChargeCents + line.lineServiceTaxCents);
  }
  const weights = lines.map((line) => line.lineSubtotalCents - line.lineDiscountCents);
  return allocateProportionally(options.serviceChargeCents, weights);
}

/**
 * Break one priced line's already-computed totals into one `UnitAmount` per
 * unit of quantity, so a split payment can charge exactly a subset of a line.
 *
 * The per-unit price itself never has a remainder (modifiers are already
 * applied per unit before `lineSubtotalCents = effectiveUnitPriceCents *
 * quantity`), but the line's discount, tax and service-charge shares do not
 * divide evenly — `allocateProportionally` spreads the remainder the same
 * remainder-safe way the order-level totals already do, so summing every
 * unit's figures back up reproduces the line's totals exactly.
 */
export function computeOrderItemUnitAmounts(line: SplittableLine, quantity: number): UnitAmount[] {
  if (quantity <= 0) return [];
  const equalWeights = new Array<number>(quantity).fill(1);
  const subtotals = allocateProportionally(line.lineSubtotalCents, equalWeights);
  const discounts = allocateProportionally(line.lineDiscountCents, equalWeights);
  const itemDiscounts = allocateProportionally(line.lineItemDiscountCents, equalWeights);
  const taxes = allocateProportionally(line.lineTaxCents, equalWeights);
  const serviceCharges = allocateProportionally(line.lineServiceChargeCents, equalWeights);
  const serviceTaxes = allocateSigned(line.lineServiceTaxCents, equalWeights);

  return equalWeights.map((_, index) => {
    const subtotalCents = subtotals[index]!;
    const discountCents = discounts[index]!;
    const itemDiscountCents = itemDiscounts[index]!;
    const taxCents = taxes[index]!;
    const serviceChargeCents = serviceCharges[index]!;
    const serviceTaxCents = serviceTaxes[index]!;
    return {
      subtotalCents,
      discountCents,
      itemDiscountCents,
      taxCents,
      serviceChargeCents,
      serviceTaxCents,
      totalCents: subtotalCents - discountCents + taxCents,
    };
  });
}

export function computeOrderTotals(
  items: readonly PricedItem[],
  options: PricingOptions,
): OrderTotals {
  return computeOrderBreakdown(items, options).totals;
}

/** Outstanding balance for an order, given what has already been paid. */
export function computeBalanceDue(grandTotalCents: number, paidCents: number): number {
  return Math.max(0, grandTotalCents - paidCents);
}

export function isFullySettled(grandTotalCents: number, paidCents: number): boolean {
  return paidCents >= grandTotalCents;
}
